
/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import { Button, FormControlLabel, Switch } from "@mui/material";
import { useAppSnackbarState } from "../../../../../Basis/Components/AppSnackbarContext";
import LoadingIndicator from "../../../../../Basis/Components/LoadingIndicator";
import useExecute from "../../../../../Basis/Utils/useExecute";
import AendereEigeneFirmendaten, { AendereEigeneFirmendatenInput } from "../../../../../Management/Mandanten/Commands/AendereEigeneFirmendaten";
import LadeEigeneFirmendaten from "../../../../../Management/Mandanten/Queries/LadeEigeneFirmendaten";
import { translationKeys, useTranslation } from "../../../Internationalisierung/i18nEinstellungen";
import ActionsBar from "../ActionsBar";
import { useSchnelleinstiegContext } from "../SchnelleinstiegContext";
import SchrittCount from "../SchrittCount";
import SchrittHeader from "../SchrittHeader";
import SchrittText from "../SchrittText";
import SchrittTitel from "../SchrittTitel";
import { QueryResult } from "../utils";
import FirmendatenInput from "./FirmendatenInput";
import { Feld, FELDER_GRUPPEN, FelderGruppe } from "./types";

const FELDER: { [Key in FelderGruppe]: Feld[] } = {
    name: [
        ["name1", true],
        ["name2", false],
        ["name3", false],
    ],
    anschrift: [
        ["strasse", true],
        ["postleitzahl", true],
        ["ort", true],
        ["land", true],
        ["region", true],
    ],
    kommunikation: [
        ["eMail", true],
        ["telefon", false],
        ["mobil", false],
        ["fax", false],
        ["homepage", false],
    ],
    rechnungsadresse: [
        ["rechnungsadresseName1", true],
        ["rechnungsadresseName2", false],
        ["rechnungsadresseName3", false],
        ["rechnungsadresseStrasse", true],
        ["rechnungsadressePostleitzahl", true],
        ["rechnungsadresseOrt", true],
        ["rechnungsadresseLand", true],
        ["rechnungsadresseRegion", true],
        ["rechnungsadresseEMail", true],
        ["rechnungsadresseTelefon", false],
        ["rechnungsadresseFax", false],
    ]
};

export default function SchnelleinstiegFirmendaten() {
    const { t } = useTranslation();
    const { WeiterButton } = useSchnelleinstiegContext();
    const [result, setResult] = React.useState<QueryResult<AendereEigeneFirmendatenInput>>({ loading: true })
    const [gruppenIndex, setGruppenIndex] = React.useState(0);
    const aktuelleGruppe = FELDER_GRUPPEN[gruppenIndex];
    const sichbareFelder = FELDER[aktuelleGruppe];
    const { items } = useAppSnackbarState();
    const kannZuFehlerSpringen = React.useRef(true);

    // Im Fehlerfall: Gehe zur Gruppe des ersten fehlerhaften Feldes
    React.useEffect(() => {
        if (!kannZuFehlerSpringen.current) {
            return;
        }

        const feldName = items
            .filter(item => item.level === "Error")
            .map(item => item.path[item.path.length - 1])[0]?.toLowerCase();

        if (feldName) {
            for (const [gruppe, felder] of Object.entries(FELDER)) {
                if (felder.map(([n, _]) => n.toLowerCase()).includes(feldName)) {
                    const idx = FELDER_GRUPPEN.findIndex(g => g === gruppe);
                    if (idx !== -1) {
                        setGruppenIndex(idx);
                        kannZuFehlerSpringen.current = false;
                    }

                    return;
                }
            }
        }
    }, [items]);

    new LadeEigeneFirmendaten(data => {
        setResult({
            loading:
                false,
            data: {
                mandantId: data.mandantId,
                geaendert: data.mandantGeaendert,
                eMail: data.eMail,
                hatRechnungsadresse: data.hatRechnungsadresse,
                land: data.land ?? "",
                name1: data.name1,
                ort: data.ort ?? "",
                postleitzahl: data.postleitzahl ?? "",
                region: data.region ?? "",
                strasse: data.strasse ?? "",
                anzahlMitarbeitendeId: data.anzahlMitarbeitendeId,
                bank: data.bank,
                bic: data.bic,
                fax: data.fax,
                mobil: data.mobil,
                homepage: data.homepage,
                iban: data.iban,
                name2: data.name2,
                name3: data.name3,
                rechnungsadresseEMail: data.rechnungsadresseEMail,
                rechnungsadresseFax: data.rechnungsadresseFax,
                rechnungsadresseMobil: data.rechnungsadresseMobil,
                rechnungsadresseLand: data.rechnungsadresseLand,
                rechnungsadresseName1: data.rechnungsadresseName1,
                rechnungsadresseName2: data.rechnungsadresseName2,
                rechnungsadresseName3: data.rechnungsadresseName3,
                rechnungsadresseOrt: data.rechnungsadresseOrt,
                rechnungsadressePostleitzahl: data.rechnungsadressePostleitzahl,
                rechnungsadresseRegion: data.rechnungsadresseRegion,
                rechnungsadresseSteuernummer: data.rechnungsadresseSteuernummer,
                rechnungsadresseStrasse: data.rechnungsadresseStrasse,
                rechnungsadresseTelefon: data.rechnungsadresseTelefon,
                rechnungsadresseUmsatzsteuerIdNr: data.rechnungsadresseUmsatzsteuerIdNr,
                schwerpunktId: data.schwerpunktId,
                schwerpunktSonstiges: data.schwerpunktSonstiges,
                steuernummer: data.steuernummer,
                telefon: data.telefon,
                umsatzsteuerIdNr: data.umsatzsteuerIdNr,
                zusatzangabe1: data.zusatzangabe1,
                zusatzangabe2: data.zusatzangabe2,
                zusatzangabe3: data.zusatzangabe3,
                mandantBezeichnungAnpassen: true,
            }
        });
    });

    const aendereEigeneFirmendaten = useExecute(new AendereEigeneFirmendaten([]));

    function handleChange<K extends keyof AendereEigeneFirmendatenInput>(key: K) {
        return (value: AendereEigeneFirmendatenInput[K]) => {
            setResult(prev => {
                if ("data" in prev) {
                    return {
                        ...prev,
                        data: {
                            ...prev.data,
                            [key]: value,
                        }
                    }
                }

                return prev;
            })
        };
    }

    function handleWeiterClick() {
        setGruppenIndex(prev => Math.min(prev + 1, FELDER_GRUPPEN.length - 1));
    }

    function handleZurueckClick() {
        setGruppenIndex(prev => Math.max(prev - 1, 0));
    }

    function istPflichtfeld(key: keyof AendereEigeneFirmendatenInput) {
        return sichbareFelder.find(([k]) => key === k)?.[1];
    }

    if (result.loading) {
        return <LoadingIndicator loading />
    }

    const kannWeiter = (() => {
        if (result.loading) {
            return false;
        }

        if ((aktuelleGruppe === "rechnungsadresse") && !result.data.hatRechnungsadresse) {
            return true;
        }

        return sichbareFelder
            .filter(([, istPflichtfeld]) => istPflichtfeld)
            .every(([key]) => !!result.data[key])
    })();

    return (
        <>
            <SchrittHeader>
                <SchrittTitel>
                    {t(translationKeys.schnelleinstiegFirmendaten.titel[aktuelleGruppe])}
                </SchrittTitel>
            </SchrittHeader>

            <SchrittText>{t(translationKeys.schnelleinstiegFirmendaten.text[aktuelleGruppe])}</SchrittText>
            {aktuelleGruppe === "rechnungsadresse" &&
                <FormControlLabel
                    label={t(translationKeys.schnelleinstiegFirmendaten.hatRechnungsadresse)}
                    control={
                        <Switch
                            checked={result.data.hatRechnungsadresse}
                            onChange={(_, checked) => handleChange("hatRechnungsadresse")(checked)}
                        />
                    }
                />
            }

            {(() => {
                if ((aktuelleGruppe !== "rechnungsadresse") || ((aktuelleGruppe === "rechnungsadresse") && result.data.hatRechnungsadresse)) {
                    return sichbareFelder.map(([key], idx) => (
                        <FirmendatenInput
                            key={key}
                            name={key}
                            data={result.data}
                            onChange={handleChange}
                            autoFocus={idx === 0}
                            required={istPflichtfeld(key)}
                        />
                    ))
                }
            })()}

            <ActionsBar>
                {gruppenIndex > 0 && (
                    <Button
                        variant="text"
                        color="secondary"
                        onClick={handleZurueckClick}
                    >
                        {t(translationKeys.schnelleinstiegFirmendaten.zurueck)}
                    </Button>
                )}
                <SchrittCount akutellerSchritt={gruppenIndex + 1} schritteGesamt={FELDER_GRUPPEN.length} />
                {gruppenIndex < FELDER_GRUPPEN.length - 1 ? (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleWeiterClick}
                        disabled={!kannWeiter}
                    >
                        {t(translationKeys.schnelleinstiegFirmendaten.weiter)}
                    </Button>
                ) : (
                    <WeiterButton
                        onClick={() => {
                            kannZuFehlerSpringen.current = true;
                            return aendereEigeneFirmendaten(result.data)
                        }}
                        disabled={!kannWeiter}
                    />
                )}
            </ActionsBar>
        </>
    )
}
